import { apiBase } from "@apis/index";
import { VEX_SNAPSHOT_ATTACHMENT_TARGET, VEX_SNAPSHOT_SOURCE } from "@constants/vex";
import queryKeys from "@configs/react-query/query-key";

const SUCCESS_CODE = "00000";

/**
 * Unwrap BaseResponse: { code, message, data }
 * Older endpoints may return bare payload — keep compatible.
 */
const unwrapData = (response) => {
  if (response && typeof response === "object" && "code" in response && "data" in response) {
    if (response.code !== SUCCESS_CODE) {
      const error = new Error(response.message || "VEX API error");
      error.response = { data: response, status: response.code === "20700" ? 403 : 400 };
      error.vexCode = response.code;
      throw error;
    }
    return response.data;
  }
  return response;
};

/**
 * GET /vex
 *
 * Query params:
 *   scanToolCd       (required) clarity | fossid | clarityair | sbom
 *   verificationSeq  (required)
 *   componentName    (optional — modal scope)
 *   componentVersion (optional — with componentName; "" allowed)
 *
 * Response data: { list: Vex[], progress: { processed, total } }
 */
export const fetchVexList = async ({
  scanToolCd,
  verificationSeq,
  componentName,
  componentVersion,
} = {}) => {
  const params = {
    scanToolCd,
    verificationSeq,
  };
  if (componentName != null && componentName !== "") {
    params.componentName = componentName;
    params.componentVersion = componentVersion ?? "";
  }

  const response = await apiBase({
    url: "/vex",
    method: "GET",
    params,
  });
  return unwrapData(response);
};

/**
 * GET /vex/progress — same query as list
 *
 * Response data: {
 *   total, processed, affected, notAffected, fixed, underInvestigation
 * }
 * processed = entries with a VEX decision other than AFFECTED
 */
export const fetchVexProgress = async ({
  scanToolCd,
  verificationSeq,
  componentName,
  componentVersion,
} = {}) => {
  const params = { scanToolCd, verificationSeq };
  if (componentName != null && componentName !== "") {
    params.componentName = componentName;
    params.componentVersion = componentVersion ?? "";
  }

  const response = await apiBase({
    url: "/vex/progress",
    method: "GET",
    params,
  });
  return unwrapData(response);
};

/**
 * GET /vex/{vexSeq}
 */
export const fetchVexBySeq = async (vexSeq) => {
  const response = await apiBase({
    url: `/vex/${vexSeq}`,
    method: "GET",
  });
  return unwrapData(response);
};

/**
 * PUT /vex/{vexSeq}
 *
 * Body:
 * {
 *   vexStatus: "UNDER_INVESTIGATION" | "AFFECTED" | "NOT_AFFECTED" | "FIXED",
 *   justification: string | null,  // required when NOT_AFFECTED
 *   additionalNote: string | null
 * }
 */
export const updateVex = async (vexSeq, body) => {
  const response = await apiBase({
    url: `/vex/${vexSeq}`,
    method: "PUT",
    data: body,
  });
  return unwrapData(response);
};

/** PUT the analyst decision for a frozen snapshot row. */
export const updateVexSnapshot = async (source, vexSeq, body) => {
  if (!Object.values(VEX_SNAPSHOT_SOURCE).includes(source)) {
    throw new Error("Unsupported VEX snapshot source");
  }
  const response = await apiBase({
    url: `/vex/${source}/${vexSeq}`,
    method: "PUT",
    data: body,
  });
  return unwrapData(response);
};

/**
 * POST /file_group_upload — VEX attachments
 * targetTypeCd=VEX, targetSeq=vexSeq
 */
export const uploadVexAttachments = async ({ vexSeq, fileGroupNo = 0, files = [] } = {}) => {
  const formData = new FormData();
  formData.append("targetTypeCd", "VEX");
  formData.append("targetSeq", vexSeq);
  formData.append("fileGroupNo", fileGroupNo || 0);

  files.forEach((entry, index) => {
    const file = entry?.file instanceof File ? entry.file : entry;
    if (file instanceof File) {
      formData.append(`file_${index}`, file, file.name);
    }
  });

  return apiBase({
    url: "/file_group_upload",
    method: "POST",
    data: formData,
    headers: { "Content-Type": "multipart/form-data" },
  });
};

/** Upload evidence to one frozen snapshot row; evidence is never propagated to another row. */
export const uploadVexSnapshotAttachments = async ({
  source,
  vexSeq,
  fileGroupNo = 0,
  files = [],
} = {}) => {
  const targetTypeCd = VEX_SNAPSHOT_ATTACHMENT_TARGET[source];
  if (!targetTypeCd) {
    throw new Error("Unsupported VEX snapshot source");
  }

  const formData = new FormData();
  formData.append("targetTypeCd", targetTypeCd);
  formData.append("targetSeq", vexSeq);
  formData.append("fileGroupNo", fileGroupNo || 0);

  files.forEach((entry, index) => {
    const file = entry?.file instanceof File ? entry.file : entry;
    if (file instanceof File) {
      formData.append(`file_${index}`, file, file.name);
    }
  });

  return apiBase({
    url: "/file_group_upload",
    method: "POST",
    data: formData,
    headers: { "Content-Type": "multipart/form-data" },
  });
};

/**
 * POST /vex/version-analysis — advisory evidence from Genesis-backed cache
 *
 * Body:
 *   cves             (required, string[])
 *   componentName    (required)
 *   componentVersion (required; "" allowed)
 *
 * Response data: [{
 *   cveId, componentName, installedVersion,
 *   withinAffectedRange: boolean | null,
 *   matches: [{ vendor, product, criteria, versionStart, versionStartInclusive,
 *               versionEnd, versionEndInclusive, affectedVersionRange, fixedVersion }]
 * }]
 */
export const fetchVexVersionAnalysis = async ({ cves = [], componentName, componentVersion } = {}) => {
  const normalized = [
    ...new Set(
      (Array.isArray(cves) ? cves : [cves])
        .map((cve) => (cve || "").trim())
        .filter(Boolean),
    ),
  ];
  if (normalized.length === 0) {
    return [];
  }

  const response = await apiBase({
    url: "/vex/version-analysis",
    method: "POST",
    data: {
      cves: normalized,
      componentName,
      componentVersion: componentVersion ?? "",
    },
  });
  return unwrapData(response);
};

export const EMPTY_VEX_SNAPSHOT = { list: [], progress: null };

/** Snapshot APIs may return Vex[] or { list, progress }. */
export const normalizeVexSnapshotPayload = (data) => {
  if (Array.isArray(data)) return { list: data, progress: null, verificationStatusCd: null };
  if (data && typeof data === "object") {
    return {
      list: Array.isArray(data.list) ? data.list : [],
      progress: data.progress ?? null,
      verificationStatusCd: data.verificationStatusCd ?? null,
    };
  }
  return { list: [], progress: null, verificationStatusCd: null };
};

/** Shared React Query key so table badges and VexProgressChip reuse one snapshot fetch. */
export const vexSnapshotQueryKey = (frozenVexSnapshot, scope = "table") => [
  queryKeys.vexSnapshot,
  frozenVexSnapshot?.source,
  frozenVexSnapshot?.verificationReportSeq,
  frozenVexSnapshot?.mergedSbomSeq,
  frozenVexSnapshot?.productSeq,
  frozenVexSnapshot?.filterByGroupProduct ? true : false,
  frozenVexSnapshot?.groupProductSeq ?? "",
  frozenVexSnapshot?.projectId ?? "",
  frozenVexSnapshot?.verificationVersion ?? "",
  frozenVexSnapshot?.verificationType ?? "",
  scope,
];

const fetchSnapshot = async (url, params) => {
  const response = await apiBase({ url, method: "GET", params });
  const data = unwrapData(response);
  return normalizeVexSnapshotPayload(data);
};

/**
 * GET /vex/verification-report §5.7.1 — frozen VEX snapshot (read-only)
 *
 * Query: verificationReportSeq (required), verificationReportDetailSeq (optional row filter)
 * Response data: { list: Vex[], progress: VexProgressDTO } | Vex[] (legacy)
 */
export const fetchVexVerificationReportSnapshot = ({
  verificationReportSeq,
  verificationReportDetailSeq,
} = {}) => {
  const params = { verificationReportSeq };
  if (verificationReportDetailSeq != null) {
    params.verificationReportDetailSeq = verificationReportDetailSeq;
  }
  return fetchSnapshot("/vex/verification-report", params);
};

/**
 * GET /vex/merged-sbom §5.7.2 — frozen VEX snapshot (read-only)
 *
 * Query: mergedSbomSeq (required), mergedSbomComponentSeq (optional row filter)
 * Response data: { list: Vex[], progress: VexProgressDTO } | Vex[] (legacy)
 */
export const fetchVexMergedSbomSnapshot = ({ mergedSbomSeq, mergedSbomComponentSeq } = {}) => {
  const params = { mergedSbomSeq };
  if (mergedSbomComponentSeq != null) {
    params.mergedSbomComponentSeq = mergedSbomComponentSeq;
  }
  return fetchSnapshot("/vex/merged-sbom", params);
};

/**
 * GET /vex/product §5.7.3 — frozen VEX snapshot (read-only)
 *
 * Query:
 *   productSeq                 (required)
 *   mergedProductComponentSeq  (optional row filter)
 *   filterByGroupProduct       (optional; true when viewing a product group)
 *   groupProductSeq            (optional; omit for the default group)
 *   projectId                  (optional; for verification-specific filtering)
 *   verificationVersion        (optional; for verification-specific filtering)
 *   verificationType           (optional; for verification-specific filtering)
 *
 * Response data: { list: Vex[], progress: VexProgressDTO } | Vex[] (legacy)
 */
export const fetchVexProductSnapshot = ({
  productSeq,
  mergedProductComponentSeq,
  filterByGroupProduct = false,
  groupProductSeq,
  projectId,
  verificationVersion,
  verificationType,
} = {}) => {
  const params = { productSeq };
  if (mergedProductComponentSeq != null) {
    params.mergedProductComponentSeq = mergedProductComponentSeq;
  }
  if (filterByGroupProduct) {
    params.filterByGroupProduct = true;
    if (groupProductSeq != null && groupProductSeq !== "") {
      params.groupProductSeq = groupProductSeq;
    }
  }
  // Add new verification-specific parameters if provided
  if (projectId != null) {
    params.projectId = projectId;
  }
  if (verificationVersion != null) {
    params.verificationVersion = verificationVersion;
  }
  if (verificationType != null) {
    params.verificationType = verificationType;
  }
  return fetchSnapshot("/vex/product", params);
};

/**
 * Dispatch a frozen snapshot fetch from a `frozenVexSnapshot` descriptor.
 * `componentSeq` is the row PK of that source (see VEX_SNAPSHOT_COMPONENT_SEQ_FIELD).
 */
export const fetchVexSnapshot = ({ source, ...parentSeqs } = {}, componentSeq = null) => {
  switch (source) {
    case VEX_SNAPSHOT_SOURCE.VERIFICATION_REPORT:
      return fetchVexVerificationReportSnapshot({
        verificationReportSeq: parentSeqs.verificationReportSeq,
        verificationReportDetailSeq: componentSeq,
      });
    case VEX_SNAPSHOT_SOURCE.MERGED_SBOM:
      return fetchVexMergedSbomSnapshot({
        mergedSbomSeq: parentSeqs.mergedSbomSeq,
        mergedSbomComponentSeq: componentSeq,
      });
    case VEX_SNAPSHOT_SOURCE.PRODUCT:
      return fetchVexProductSnapshot({
        productSeq: parentSeqs.productSeq,
        mergedProductComponentSeq: componentSeq,
        filterByGroupProduct: Boolean(parentSeqs.filterByGroupProduct),
        groupProductSeq: parentSeqs.groupProductSeq,
        projectId: parentSeqs.projectId,
        verificationVersion: parentSeqs.verificationVersion,
        verificationType: parentSeqs.verificationType,
      });
    default:
      return Promise.resolve(EMPTY_VEX_SNAPSHOT);
  }
};

/**
 * GET /vex/import-sources/{scanToolCd} — previous DONE verifications (Phase 3)
 *
 * BE filters: verificationStatusCd === "DONE", verificationVersion < target,
 * sorted by verificationVersion DESC. Same project as current verification.
 *
 * Query params:
 *   projectSeq            (required)
 *   targetVerificationSeq (required; current verification, excluded by BE)
 *
 * Response data: array of verification objects (same fields as per-tool list APIs)
 */
export const fetchVexImportSources = async ({ scanToolCd, projectSeq, targetVerificationSeq } = {}) => {
  const response = await apiBase({
    url: `/vex/import-sources/${scanToolCd}`,
    method: "GET",
    params: {
      projectSeq,
      targetVerificationSeq,
    },
  });
  return unwrapData(response);
};

/**
 * POST /vex/import — copy VEX decisions from a previous verification
 *
 * Body: { scanToolCd, targetVerificationSeq, sourceVerificationSeq }
 * Response data (suggested): { imported, skipped }
 */
export const importVexFromPreviousVersion = async ({
  scanToolCd,
  targetVerificationSeq,
  sourceVerificationSeq,
} = {}) => {
  const response = await apiBase({
    url: "/vex/import",
    method: "POST",
    data: {
      scanToolCd,
      targetVerificationSeq,
      sourceVerificationSeq,
    },
  });
  return unwrapData(response);
};

/**
 * GET /file_info_by_file_group_no/{fileGroupNo}
 */
export const fetchVexAttachments = async (fileGroupNo) => {
  if (!fileGroupNo) return [];
  const response = await apiBase({
    url: `/file_info_by_file_group_no/${fileGroupNo}`,
    method: "GET",
  });
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.object)) return response.object;
  return [];
};

/**
 * DELETE /file_info/{fileInfoSeq}
 */
export const deleteVexAttachment = async (fileInfoSeq) => {
  return apiBase({
    url: `/file_info/${fileInfoSeq}`,
    method: "DELETE",
  });
};
